/* Shared props and text helpers for the sheet pages. */

import { ReactNode } from 'react';
import type { SheetSection } from './content/sheetText';
import type { Catalog, Weapon } from './catalog';
import type { BlankPart, Character, Stats, Track } from './model';

/** The class choices a page can open for editing. */
export type ClassChoice = 'patron' | 'element';

export interface PageProps {
  c: Character;
  catalog: Catalog;
  stats: Stats;
  /** Areas lit up while the builder is on the step that fills them. */
  highlight: SheetSection[];
  /** Crops the page to its lit areas (the builder preview's close-up). */
  closeUp?: boolean;
  onMark?: (track: Track, count: number) => void;
  /** Changes other parts of the character from the page (the Martial Stances page's stances and layout). */
  onSet?: (patch: Partial<Character>) => void;
  /** Shows gear buttons that open the builder step for an area. */
  onEdit?: (section: SheetSection) => void;
  /** Shows a Transform button on each Beastform the Druid can take. */
  onBeastform?: (form: string) => void;
  /** Makes a class choice written on a page (the Warlock's patron, the Elementalist's element) a button that opens its dialog. */
  onChoose?: (choice: ClassChoice) => void;
  /** Parts a printed copy leaves empty; `c` already has them emptied, except Inventory and Trait marks. */
  blank?: readonly BlankPart[];
}

export const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const signed = (n: number | null | undefined) => (n == null ? '' : n > 0 ? `+${n}` : String(n));

export const damageLine = (w: Weapon) =>
  w.damage ? `${w.damage} ${w.damageType.startsWith('Mag') ? 'mag' : w.damageType === 'Tech' ? 'tech' : 'phy'}` : '';

/** "Rapier - Presence Melee - d8 phy - One-Handed", as the guides list weapons. */
export const weaponSummary = (w: Weapon) =>
  [w.name, [w.trait, w.range].filter(Boolean).join(' '), damageLine(w), w.burden].filter(Boolean).join(' - ');

/** Splits "Quick: When you make..." into its name and text. */
export function splitFeature(text?: string | null): [string, string] {
  const i = (text ?? '').indexOf(': ');
  return i > 0 ? [text!.slice(0, i), text!.slice(i + 2)] : ['', text ?? ''];
}

export function FeatureText({ text }: { text?: string | null }) {
  const [name, rest] = splitFeature(text);
  return (
    <>
      {name && <em className="cs-feature-name">{name}:</em>} {emphasize(rest)}
    </>
  );
}

// The sheets set costs and dice in bold: "Spend 3 Hope", "mark a Stress", "d6".
const EMPHASIS = /(spend (?:\d+|a|any number of) (?:Hope|Favor|Focus)|mark (?:a|any number of|\d+) Stress|\bd\d+s?\b)/gi;

export function emphasize(text: string): ReactNode {
  return text.split(EMPHASIS).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}
